import { createAsyncThunk } from "@reduxjs/toolkit";
import { type Points, Status, type TierData } from "@store/slices/loyaltyCoreSlice/loyaltyCoreSlice.types";

import { globalEventsHandler } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

type FetchUserLoyaltyProfilePayload = {
  dispatchUpdateEvent?: boolean;
  dispatchCodeRegistrationEvent?: boolean;
  skipCache?: boolean;
};

export const fetchUserLoyaltyProfile = createAsyncThunk<
  {
    points: Points;
    tier: TierData;
  },
  FetchUserLoyaltyProfilePayload,
  { rejectValue: { message: string } }
>(
  "loyaltyCore/fetchUserLoyaltyProfile",
  async (
    { skipCache, dispatchUpdateEvent, dispatchCodeRegistrationEvent }: FetchUserLoyaltyProfilePayload,
    { rejectWithValue }
  ) => {
    const user = getUser();
    const loyaltyApi = LoyaltyApi.init();

    if (!user?.uuid || !user?.ciam_token) {
      return rejectWithValue({ message: "Error while fetching loyalty profile - no user data" });
    }

    try {
      const {
        data: {
          points,
          expire_at,
          points_to_expire,
          tier_image_url,
          tier_name,
          next_tier_name,
          points_to_next_tier,
          current_tier_secured_till,
          current_tier_assignment_date,
          points_to_maintain_current_tier,
          cumulative_points,
        },
      } = await loyaltyApi.getLoyaltyProfile(user.uuid, user.ciam_token, skipCache);

      if (dispatchUpdateEvent) {
        globalEventsHandler.updateLoyaltyPoints().register(points);
      }

      if (dispatchCodeRegistrationEvent) {
        dispatchTrackingEvent(TrackingEvents.TrackingCodeSubmitSuccess, { user_points_balance: points });
      }

      return {
        points: { balance: points, expire_at, status: Status.COMPLETED, points_to_expire },
        tier: {
          image_url: tier_image_url ?? "",
          name: tier_name,
          next_tier_name: next_tier_name ?? "",
          points_to_next_tier,
          current_tier_secured_till,
          current_tier_assignment_date,
          points_to_maintain_current_tier,
          cumulative_points,
        },
      };
    } catch (error) {
      console.error(error);
      if (typeof error === "object" && error !== null) {
        const { message } = error as Record<string, unknown>;
        if (typeof message === "string") {
          return rejectWithValue({ message });
        }
      }
      return rejectWithValue({ message: "Error while fetching loyalty profile" });
    }
  }
);
